import { resolve } from "node:path";
import { pathToFileURL } from "node:url";
import {
	importPolicyFromBillingConfig,
	runBillingCustomerImportWithPostgres,
} from "../../../billing/import/assemble.ts";
import { createBillingSqlExecutor } from "../../../billing/import/postgres.ts";
import type { BillingImportRunReport } from "../../../billing/import/types.ts";
import { resolveBillingWebhookApplicationId } from "../../../billing/ingestion/application-id.ts";
import {
	applyGeneratorProjectEnv,
	findGeneratorConfigPath,
	loadGeneratorConfig,
} from "../../../generator/config.ts";
import type { ResolvedGeneratorDatabaseAuthority } from "../../../generator/database-authority.ts";
import { resolveGeneratorDatabaseAuthority } from "../../../generator/database-authority.ts";
import { createPostgresPool } from "../../../postgres/driver.ts";
import type { AthenaBillingConfig } from "../../../v3-client-core.ts";
import { AthenaCliError } from "../../errors.ts";
import { CliExitCode } from "../../exit-code.ts";
import type {
	BillingReconcileSubjectsCommand,
	BillingWebhooksCommand,
} from "../../types.ts";

export interface ExecuteBillingReconcileSubjectsInput {
	cwd: string;
	parsed: BillingReconcileSubjectsCommand;
}

function readOptionalBillingFromUnknown(
	value: unknown,
): AthenaBillingConfig | undefined {
	if (value == null || typeof value !== "object") {
		return undefined;
	}
	const record = value as Record<string, unknown>;
	if (record.billing != null && typeof record.billing === "object") {
		return record.billing as AthenaBillingConfig;
	}
	if (record.default != null) {
		return readOptionalBillingFromUnknown(record.default);
	}
	if (record.config != null) {
		return readOptionalBillingFromUnknown(record.config);
	}
	return undefined;
}

async function loadProjectBillingConfig(input: {
	configPath?: string;
	cwd: string;
}): Promise<AthenaBillingConfig | undefined> {
	const resolved = input.configPath
		? resolve(input.cwd, input.configPath)
		: findGeneratorConfigPath(input.cwd);
	if (!resolved) {
		return undefined;
	}
	const module = await import(
		`${pathToFileURL(resolved).href}?cacheBust=${Date.now()}`
	);
	return readOptionalBillingFromUnknown(module);
}

async function resolveBillingDatabaseAuthority(input: {
	configPath?: string;
	cwd: string;
}): Promise<{
	authority: ResolvedGeneratorDatabaseAuthority;
	restoreEnv: () => void;
}> {
	const restoreEnv = applyGeneratorProjectEnv(input.cwd);
	try {
		const loaded = await loadGeneratorConfig({
			applyProjectEnv: false,
			configPath: input.configPath,
			cwd: input.cwd,
			restoreProjectEnv: false,
		});
		const authority = resolveGeneratorDatabaseAuthority({
			applyProjectEnv: false,
			cwd: input.cwd,
			loaded,
			mode: "direct",
		});
		return { authority, restoreEnv };
	} catch (error) {
		restoreEnv();
		throw error;
	}
}

export async function executeBillingReconcileSubjects(
	input: ExecuteBillingReconcileSubjectsInput,
): Promise<BillingImportRunReport> {
	const cwd = input.cwd;
	const { authority, restoreEnv } = await resolveBillingDatabaseAuthority({
		configPath: input.parsed.configPath,
		cwd,
	});
	try {
		const provider = authority.provider;
		if (provider.kind !== "postgres" || provider.mode !== "direct") {
			throw new AthenaCliError({
				code: "BILLING001",
				exitCode: CliExitCode.Runtime,
				hint: "Use the same athena.config.ts / DATABASE_URL that `athena-js migrate` uses.",
				message: `billing reconcile-subjects requires postgres/direct (got ${provider.kind}/${provider.mode}).`,
			});
		}
		if (!provider.connectionString) {
			throw new AthenaCliError({
				code: "BILLING001",
				exitCode: CliExitCode.Runtime,
				hint: "Set DATABASE_URL or provider.connectionString, then retry.",
				message: "billing reconcile-subjects requires a reachable database.",
			});
		}
		const billing = await loadProjectBillingConfig({
			configPath: input.parsed.configPath,
			cwd,
		});
		const pool = await createPostgresPool(provider.connectionString, {
			max: 4,
			min: 0,
		});
		try {
			const sql = createBillingSqlExecutor(pool);
			return await runBillingCustomerImportWithPostgres({
				configuredProviders: billing?.providers,
				connectionId: input.parsed.connectionId,
				cursor: input.parsed.cursor,
				customerId: input.parsed.customerId,
				dryRun: input.parsed.dryRun,
				includeAmbiguous: input.parsed.includeAmbiguous,
				limit: input.parsed.limit,
				limits: {
					maxCustomers: input.parsed.maxCustomers,
					maxDurationMs: input.parsed.maxDurationMs,
					maxPages: input.parsed.maxPages,
					pageSize: input.parsed.limit,
				},
				pool,
				policy: importPolicyFromBillingConfig(billing),
				provider: input.parsed.provider,
				sql,
				subjectId: input.parsed.subjectId,
				testMode: billing?.testMode,
				trigger: "manual",
			});
		} finally {
			await pool.end();
		}
	} finally {
		restoreEnv();
	}
}

export interface ExecuteBillingWebhooksInput {
	cwd: string;
	parsed: BillingWebhooksCommand;
}

export async function executeBillingWebhooks(
	input: ExecuteBillingWebhooksInput,
): Promise<Record<string, unknown>> {
	const restoreEnv = applyGeneratorProjectEnv(input.cwd);
	try {
		const billing = await loadProjectBillingConfig({
			configPath: input.parsed.configPath,
			cwd: input.cwd,
		});
		const { normalizeAthenaBillingIngestion } = await import(
			"../../../billing/ingestion/config.ts"
		);
		const { resolveBillingIngressEndpoints } = await import(
			"../../../billing/ingestion/urls.ts"
		);
		const ingestion = normalizeAthenaBillingIngestion(billing?.ingestion);
		const endpoints = resolveBillingIngressEndpoints({
			appUrl: undefined,
			publicBaseUrl: ingestion.webhooks.publicBaseUrl,
		});
		if (input.parsed.action !== "reconcile" || input.parsed.dryRun) {
			return {
				action: input.parsed.action,
				classicUrl: endpoints?.classicUrl,
				connectionId: input.parsed.connectionId,
				dryRun: input.parsed.dryRun,
				eventsUrl: endpoints?.eventsUrl,
				management: ingestion.webhooks.management,
				provider: input.parsed.provider ?? "mollie",
			};
		}
		const loaded = await loadGeneratorConfig({
			applyProjectEnv: false,
			configPath: input.parsed.configPath,
			cwd: input.cwd,
			restoreProjectEnv: false,
		});
		const authority = resolveGeneratorDatabaseAuthority({
			applyProjectEnv: false,
			cwd: input.cwd,
			loaded,
			mode: "direct",
		});
		const provider = authority.provider;
		if (provider.kind !== "postgres" || provider.mode !== "direct") {
			throw new AthenaCliError({
				code: "BILLING001",
				exitCode: CliExitCode.Runtime,
				hint: "Use the same athena.config.ts / DATABASE_URL that `athena-js migrate` uses.",
				message: `billing webhooks requires postgres/direct (got ${provider.kind}/${provider.mode}).`,
			});
		}
		if (!provider.connectionString) {
			throw new AthenaCliError({
				code: "BILLING001",
				exitCode: CliExitCode.Runtime,
				hint: "Set DATABASE_URL or provider.connectionString, then retry.",
				message: "billing webhooks requires a reachable database.",
			});
		}
		const pool = await createPostgresPool(provider.connectionString, {
			max: 4,
			min: 0,
		});
		try {
			const sql = createBillingSqlExecutor(pool);
			const { runAutomaticBillingWebhookReconcileIfEnabled } = await import(
				"../../../billing/ingestion/automatic.ts"
			);
			const applicationId = resolveBillingWebhookApplicationId({
				client: process.env.ATHENA_CLIENT,
			});
			if (applicationId == null) {
				throw new AthenaCliError({
					code: "BILLING001",
					exitCode: CliExitCode.Runtime,
					hint: "Set ATHENA_CLIENT or createClient({ app: { id } }). Do not use a display name.",
					message:
						"billing webhooks reconcile requires a stable application id (ATHENA_CLIENT).",
				});
			}
			await runAutomaticBillingWebhookReconcileIfEnabled({
				applicationId,
				appUrl: ingestion.webhooks.publicBaseUrl,
				configuredProviders: billing?.providers,
				sql,
				testMode: billing?.testMode,
				trigger: "manual",
				webhooks: ingestion.webhooks,
			});
			return {
				action: "reconcile",
				connectionId: input.parsed.connectionId,
				dryRun: false,
				provider: input.parsed.provider ?? "mollie",
			};
		} finally {
			await pool.end();
		}
	} finally {
		restoreEnv();
	}
}
